import { ApolloQueryResult } from '@apollo/client';
import { Box, Flex, Spacer, Card, CardBody } from '@chakra-ui/react';
import React from 'react';

import { PortfolioChartDatePicker } from './PortfolioChartDatePicker';
import { ChartTimePeriod, onSelectDateRange } from './portfolioChartUtils';
import { PortfolioTimePeriodSelectButtons } from './PortfolioTimePeriodSelectButtons';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Credit facility-Level Chart Templates

// Takes 2 children: 1 for the graph and 1 for the details
type PortfolioChartWithCardsTemplateForCreditFacilityProps = {
  creditFacilityId: string;
  chartTitle: string;
  refetch: (variables: {
    [key: string]: any;
  }) => Promise<ApolloQueryResult<unknown>>;
  refetchVariables?: { [key: string]: any };
  chartDataDateRange: { chartDataStartDate: Date; chartDataEndDate: Date };
  setChartDataDateRange: (
    value: React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>,
  ) => void;
  selectedTimePeriod: ChartTimePeriod;
  onSelectTimePeriod: (timePeriod: ChartTimePeriod) => void;
  children: React.ReactNode[];
};
export const PortfolioChartWithCardsTemplateForCreditFacility = ({
  creditFacilityId,
  chartTitle,
  refetch,
  refetchVariables,
  chartDataDateRange,
  setChartDataDateRange,
  selectedTimePeriod,
  onSelectTimePeriod,
  children,
}: PortfolioChartWithCardsTemplateForCreditFacilityProps) => {
  const header = (
    <Flex mb="40px">
      <PortfolioChartDatePicker
        onSelectDateRange={(newDateRange) =>
          onSelectDateRange({
            newDateRange,
            refetch,
            refetchVariables,
            creditFacilityId,
            setChartDataDateRange,
          })
        }
        currentDateRange={chartDataDateRange}
      />
      <Spacer />
      <PortfolioTimePeriodSelectButtons
        currentSelectedTimePeriod={selectedTimePeriod}
        onSelectTimePeriod={onSelectTimePeriod}
      />
    </Flex>
  );
  return (
    <Flex direction="column" gap="1rem">
      <Typography variant="section-title" trim>
        {chartTitle}
      </Typography>
      <Card variant="outline" size="lg">
        <CardBody display="flex">
          <Box width="70%">
            {header}
            {children[0]}
          </Box>
          <Spacer />
          {children[1]}
        </CardBody>
      </Card>
    </Flex>
  );
};

// Takes 2 children: 1 for the graph and 1 for the details
type PortfolioChartWithTableTemplateForCreditFacilityProps = {
  creditFacilityId: string;
  chartTitle: string;
  refetch: (variables: {
    [key: string]: any;
  }) => Promise<ApolloQueryResult<unknown>>;
  chartDataDateRange: { chartDataStartDate: Date; chartDataEndDate: Date };
  setChartDataDateRange: (
    value: React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>,
  ) => void;
  selectedTimePeriod: ChartTimePeriod;
  onSelectTimePeriod: (timePeriod: ChartTimePeriod) => void;
  children: React.ReactNode;
};
export const PortfolioChartWithTableTemplateForCreditFacility = ({
  creditFacilityId,
  chartTitle,
  refetch,
  chartDataDateRange,
  setChartDataDateRange,
  selectedTimePeriod,
  onSelectTimePeriod,
  children,
}: PortfolioChartWithTableTemplateForCreditFacilityProps) => {
  const header = (
    <Flex mb="40px">
      <PortfolioChartDatePicker
        onSelectDateRange={(newDateRange) =>
          onSelectDateRange({
            newDateRange,
            refetch,
            creditFacilityId,
            setChartDataDateRange,
          })
        }
        currentDateRange={chartDataDateRange}
      />
      <Spacer />
      <PortfolioTimePeriodSelectButtons
        currentSelectedTimePeriod={selectedTimePeriod}
        onSelectTimePeriod={onSelectTimePeriod}
      />
    </Flex>
  );
  return (
    <Flex direction="column" gap="1rem">
      <Typography variant="section-title" trim>
        {chartTitle}
      </Typography>
      <Card variant="outline" size="lg">
        <CardBody>
          {header}
          {children}
        </CardBody>
      </Card>
    </Flex>
  );
};

// Organization-Level Chart Templates

type ChartHeaderProps = {
  right: React.ReactNode;
  left: React.ReactNode;
};
export const ChartHeader = ({ right, left }: ChartHeaderProps) => (
  <Flex justifyContent="space-between" flexWrap="wrap" gap="0.5rem">
    <Flex gap="0.5rem" flexWrap="wrap">
      {right}
    </Flex>
    <Flex gap="0.5rem" flexWrap="wrap">
      {left}
    </Flex>
  </Flex>
);

type ChartProps = {
  header: React.ReactNode;
  chart: React.ReactNode;
  sidebar: React.ReactNode;
};
export const Chart = ({ header, chart, sidebar }: ChartProps) => {
  return (
    <Flex direction="column" gap="1.5rem">
      {header}
      <Flex
        gap="1rem"
        width="100%"
        direction={['column', 'column', 'column', 'row']}
      >
        <Flex width={['100%', '100%', '100%', 'calc(75% - 1rem)']}>
          {chart}
        </Flex>
        <Flex width={['100%', '100%', '100%', '25%']}>{sidebar}</Flex>
      </Flex>
    </Flex>
  );
};
